import { useState, useEffect } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { isNil } from 'lodash'

import useGlobalConfirmBox from 'features/GlobalConfirmBox/hooks/useGlobalConfirmBox'
import { useInfinityScrollSimple } from 'hooks'
import { Link } from 'features/i18n/router'
import { User } from 'lib/_types'
import { appendList } from 'lib/helpers'
import {
  useGetBannedUsersQuery,
  useUnBanUserMutation,
} from 'store/api/yay/group'

import Avatar from '../Avatar'
import Nickname from '../Nickname'
import LoadMore from '../LoadMore'
import Button from 'components/Button'

const GroupBanListModal = ({ groupId }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { showConfirmBox } = useGlobalConfirmBox()

  const [unBan] = useUnBanUserMutation()

  const [__fromId, __fromIdSet] = useState<string>('0')
  const [__bannedUsers, __bannedUsersSet] = useState<Array<User>>([])

  const bannedUsersRes = useGetBannedUsersQuery({
    groupId,
    from: __fromId,
  })

  const loadMore = () => {
    __fromIdSet(bannedUsersRes?.data?.next_page_value?.toString?.())
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    if (bannedUsersRes?.isSuccess && bannedUsersRes?.data) {
      __bannedUsersSet((prevState) =>
        appendList(prevState, bannedUsersRes?.data?.list)
      )
    }
  }, [bannedUsersRes])

  return (
    <div className="Modal__wrapper">
      <div className="Modal__header">
        <Link className="Modal__header__close" to={location.pathname} />

        <h2 className="Modal__header__h2">
          {t('group_info_page.group_members.banned_members')}
        </h2>
      </div>

      <div className="Modal__body">
        {bannedUsersRes?.isSuccess && __bannedUsers?.length == 0 ? (
          <div className="Placeholder">
            <h6 className="Placeholder__h6">
              {t('group_ban_list.empty_title')}
            </h6>

            <p className="Placeholder__p">
              {t('group_ban_list.empty_description')}
            </p>
          </div>
        ) : (
          <dl className="RecommendUsers">
            {__bannedUsers?.map?.((x: User, i) => (
              <label
                className="RecommendUsers__item RecommendUsers__item--group-invite"
                key={i}
              >
                <div className="RecommendUsers__item__user">
                  <Avatar
                    user={x}
                    className={null}
                    wrapperClassName={null}
                    showVipCircle
                    showOnlineStatus
                  />

                  <div className="RecommendUsers__item__info">
                    <h3 className="RecommendUsers__item__h3">
                      <Nickname user={x} />
                    </h3>
                  </div>
                </div>

                <Button
                  icon="minus"
                  buttonStyle="red"
                  onClick={() => {
                    showConfirmBox({
                      title: t('group.actions.unban_dialog_title'),
                      content: t('group.actions.unban_dialog_description'),
                      confirmBoxProps: {
                        confirmButtonText: t(
                          'group.actions.unban_dialog_confirm'
                        ),
                        onOk: () => {
                          unBan({ groupId, userId: x.id }).then(() => {
                            __bannedUsersSet((prevState) => {
                              return prevState?.filter?.((y) => y?.id != x?.id)
                            })
                          })
                        },
                      },
                    })
                  }}
                >
                  {t('common.banned')}
                </Button>
              </label>
            ))}

            <LoadMore
              ref={loadMoreRef}
              isLoading={bannedUsersRes?.isFetching}
              hidden={isNil(bannedUsersRes?.data?.next_page_value)}
              className="RecommendUsers__load-more"
              onClick={() => {
                loadMore()
              }}
            />
          </dl>
        )}
      </div>
    </div>
  )
}

export default GroupBanListModal
